import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABELS } from "../lib/format";
import api from "../api/client";
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  Wallet,
  Bell,
  FileSpreadsheet,
  UserCog,
  ScrollText,
  Settings,
  LogOut,
  MessageSquare,
  Target,
  ChevronRight,
  Menu,
  X,
  Zap,
  Calculator,
  Sparkles,
  Shield,
  Archive,
  Database,
  PenTool,
  ClipboardCheck,
  Landmark,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, roles: null },
  { to: "/immeubles", label: "Projets immobiliers", icon: Building2, roles: null },
  { to: "/clients", label: "Clients & Prospects", icon: Users, roles: null },
  { to: "/souscriptions", label: "Souscriptions publiques", icon: ClipboardCheck, roles: ["admin", "secretaire", "commercial"] },
  { to: "/pipeline", label: "Pipeline", icon: Target, roles: ["admin", "secretaire", "commercial"] },
  { to: "/contrats", label: "Contrats", icon: FileText, roles: null },
  { to: "/paiements", label: "Paiements", icon: Wallet, roles: ["admin", "secretaire", "comptable", "lecture"] },
  { to: "/encaissement", label: "Encaissement rapide", icon: Zap, roles: ["admin", "secretaire", "comptable", "caissier"] },
  { to: "/caisse", label: "Caisse", icon: Landmark, roles: ["admin", "secretaire", "comptable", "caissier"] },
  { to: "/credit/simulateur", label: "Simulateur de crédit", icon: Calculator, roles: ["admin", "secretaire", "commercial", "comptable"] },
  { to: "/credit/prospects", label: "Prospects", icon: Sparkles, roles: ["admin", "secretaire", "commercial"] },
  { to: "/rappels", label: "Rappels", icon: Bell, roles: ["admin", "secretaire", "comptable", "lecture"] },
  { to: "/tickets", label: "Messagerie", icon: MessageSquare, roles: ["admin", "secretaire", "commercial", "comptable"] },
  { to: "/rapports", label: "Rapports", icon: FileSpreadsheet, roles: ["admin", "secretaire", "comptable"] },
  { to: "/documents", label: "Bibliothèque documents", icon: Archive, roles: ["admin", "secretaire", "commercial", "comptable"] },
  { to: "/agents", label: "Agents (site public)", icon: UserCog, roles: ["admin", "secretaire"] },
  { to: "/utilisateurs", label: "Utilisateurs", icon: UserCog, roles: ["admin"] },
  { to: "/parametres", label: "Paramètres", icon: Settings, roles: ["admin"] },
  { to: "/sauvegardes", label: "Sauvegardes & Exports", icon: Database, roles: ["admin"] },
  { to: "/reglages/signature", label: "Signature & Cachet", icon: PenTool, roles: ["admin"] },
  { to: "/securite", label: "Sécurité (2FA)", icon: Shield, roles: null },
  { to: "/audit", label: "Journal d'audit", icon: ScrollText, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user?.role));
  const [projets, setProjets] = useState([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [souscriptionsPending, setSouscriptionsPending] = useState(0);

  useEffect(() => {
    let mounted = true;
    api.get("/immeubles").then(({ data }) => {
      if (mounted) setProjets(Array.isArray(data) ? data : []);
    }).catch(() => {});
    // Badge souscriptions
    const loadStats = () => {
      api.get("/souscription/stats").then(({ data }) => {
        if (mounted) setSouscriptionsPending(data?.pending || 0);
      }).catch(() => {});
    };
    loadStats();
    const interval = setInterval(loadStats, 30_000); // refresh toutes les 30s
    return () => { mounted = false; clearInterval(interval); };
  }, []);

  // Ferme le drawer à chaque changement de route
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const initials = (user?.name || "")
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const currentLabel = items.find((i) => i.to === location.pathname || (i.to !== "/" && location.pathname.startsWith(i.to)))?.label || "TPCO";

  const SidebarBody = (
    <>
      <div className="h-16 flex items-center justify-between px-5 border-b border-[#E2E5F1] shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#1E3A8A] flex items-center justify-center relative shrink-0">
            <span className="font-display text-white text-base font-extrabold leading-none">T</span>
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#E91E63] border-2 border-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-[15px] font-bold text-[#0F1A3D] tracking-tight">TPCO Sénégal</span>
            <span className="text-[9px] uppercase tracking-[0.18em] text-[#E91E63] font-bold mt-1">Touba Pro Construction</span>
          </div>
        </div>
        <button
          onClick={() => setMobileOpen(false)}
          className="lg:hidden p-1.5 rounded-md hover:bg-[#F1F3FB] text-[#4B5775]"
          aria-label="Fermer le menu"
          data-testid="sidebar-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((item) => {
          const Icon = item.icon;
          const isProjets = item.to === "/immeubles";
          return (
            <div key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                    isActive
                      ? "bg-[#E8EBFA] text-[#1E3A8A] font-semibold"
                      : "text-[#4B5775] hover:bg-[#F1F3FB] hover:text-[#0F1A3D]"
                  }`
                }
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
                <span className="flex-1">{item.label}</span>
                {item.to === "/souscriptions" && souscriptionsPending > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#E91E63] text-white text-[10px] font-bold flex items-center justify-center"
                    data-testid="badge-souscriptions-pending"
                  >
                    {souscriptionsPending}
                  </span>
                )}
              </NavLink>
              {isProjets && projets.length > 0 && (
                <div className="ml-9 mt-1 mb-2 space-y-0.5 border-l border-[#E2E5F1] pl-2" data-testid="sidebar-projects-list">
                  {projets.slice(0, 8).map((p) => (
                    <NavLink
                      key={p.id}
                      to={`/immeubles/${p.id}`}
                      data-testid={`sidebar-project-${p.id}`}
                      className={({ isActive }) =>
                        `flex items-center gap-1.5 px-2 py-1.5 rounded text-[12px] transition-colors truncate ${
                          isActive
                            ? "bg-[#E8EBFA] text-[#1E3A8A] font-semibold"
                            : "text-[#4B5775] hover:bg-[#F1F3FB] hover:text-[#0F1A3D]"
                        }`
                      }
                    >
                      <ChevronRight className="w-3 h-3 shrink-0 opacity-60" />
                      <span className="truncate">{p.nom}</span>
                    </NavLink>
                  ))}
                  {projets.length > 8 && (
                    <div className="text-[10px] text-[#9CA3B5] px-2 pt-1">+{projets.length - 8} autres</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-[#E2E5F1] shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-sm font-semibold shrink-0">
            {initials || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate text-[#0F1A3D]">{user?.name}</div>
            <div className="text-[11px] text-[#4B5775] truncate">{ROLE_LABELS[user?.role]}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="p-2 rounded-md hover:bg-[#FCE4EC] text-[#4B5775] hover:text-[#E91E63] transition-colors"
            title="Déconnexion"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Sidebar desktop (≥lg) */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-[#E2E5F1] bg-white flex-col" data-testid="sidebar">
        {SidebarBody}
      </aside>

      {/* Drawer mobile (<lg) */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`lg:hidden fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] bg-white z-50 flex flex-col shadow-2xl transition-transform duration-300 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar-mobile"
        aria-hidden={!mobileOpen}
      >
        {SidebarBody}
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Header mobile (<lg) */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E2E5F1] h-14 flex items-center justify-between px-3" data-testid="topbar-mobile">
          <button
            onClick={() => setMobileOpen(true)}
            className="p-2 rounded-md hover:bg-[#F1F3FB] text-[#0F1A3D]"
            aria-label="Ouvrir le menu"
            data-testid="topbar-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 rounded-md bg-[#1E3A8A] flex items-center justify-center relative shrink-0">
              <span className="font-display text-white text-xs font-extrabold leading-none">T</span>
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#E91E63] border border-white" />
            </div>
            <span className="font-display text-sm font-bold text-[#0F1A3D] truncate">{currentLabel}</span>
          </div>
          <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white flex items-center justify-center text-[11px] font-bold shrink-0">
            {initials || "?"}
          </div>
        </header>
        <div className="fade-up">{children}</div>
      </main>
    </div>
  );
}
